/**
 * La SCALA del compenso disegnata: i gradini di un profilo (10% · 7,5% · 5% per sempre) lungo i mesi di abbonamento,
 * e dove si trova oggi un'azienda. È l'elemento che la console e partner.yumaos.com mostrano allo stesso modo: chi la
 * guarda capisce in un colpo d'occhio quanto vale un'azienda adesso e quanto varrà fra un anno.
 *
 * Qui solo la geometria (frazioni da 0 a 1), uguale per le due app; il disegno e i testi li fa ognuna col suo tema e
 * le sue traduzioni.
 */

import { percentualeDelMese, type ScaglioneCompenso } from './compensoPartner';

export interface GradinoDisegnato {
  dal_mese: number;
  /** `null` = per sempre: il gradino arriva al bordo e sfuma, perché continua oltre. */
  al_mese: number | null;
  percentuale: number;
  /** Dove comincia e quanto è largo, in frazione della larghezza (0-1). */
  inizio: number;
  larghezza: number;
  /** Altezza in frazione del gradino più alto (0-1). */
  altezza: number;
}

export interface ScalaDisegnata {
  /** Quanti mesi rappresenta la larghezza intera. */
  mesi: number;
  gradini: GradinoDisegnato[];
  /** Il mese di oggi dell'azienda, se c'è: posizione (0-1) e percentuale di quel mese. */
  oggi: { mese: number; posizione: number; percentuale: number } | null;
}

/** Quanti mesi mostrare dopo l'ultimo scaglione che finisce, perché «per sempre» si veda continuare. */
const CODA = 12;

export function disegnaScala(scaglioni: readonly ScaglioneCompenso[], meseDiOggi?: number | null): ScalaDisegnata {
  if (scaglioni.length === 0) return { mesi: 0, gradini: [], oggi: null };
  // L'ultimo mese coperto da uno scaglione che finisce (uno «per sempre» conta fino al mese prima del suo inizio).
  const ultimoFinito = Math.max(...scaglioni.map((s) => s.al_mese ?? s.dal_mese - 1));
  // La larghezza: tutti gli scaglioni che finiscono, più una coda; e il mese di oggi, se è più avanti, con un po' d'aria.
  const mesi = Math.max(ultimoFinito + CODA, meseDiOggi ? meseDiOggi + 3 : 0);
  const massima = Math.max(...scaglioni.map((s) => s.percentuale), 0.0001);
  const gradini = scaglioni.map((s) => {
    const fine = s.al_mese ?? mesi;
    return {
      dal_mese: s.dal_mese,
      al_mese: s.al_mese,
      percentuale: s.percentuale,
      inizio: (s.dal_mese - 1) / mesi,
      larghezza: (Math.min(fine, mesi) - s.dal_mese + 1) / mesi,
      altezza: s.percentuale / massima,
    };
  });
  const oggi = meseDiOggi && meseDiOggi > 0
    ? { mese: meseDiOggi, posizione: (meseDiOggi - 0.5) / mesi, percentuale: percentualeDelMese(scaglioni, meseDiOggi) }
    : null;
  return { mesi, gradini, oggi };
}
